import { useState } from 'react'
import { cn } from '@/shared/lib'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { ContentGroup } from '@/shared/ui'
import type { PaymentHistoryStatus, PaymentHistoryVariant } from '@/entities/payment'
import { PaymentHistoryRow } from '@/entities/payment'

export type PaymentHistoryItem = {
  status: PaymentHistoryStatus
  date: string
  planName: string
  amount: string
}

export type PaymentHistoryPanelProps = {
  items: PaymentHistoryItem[]
  variant?: PaymentHistoryVariant
  tone?: 'default' | 'catalog-dark'
  initialVisibleItems?: number
  className?: string
}

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.subscription.sectionTitle),
}

const dividerStyle = {
  height: designTokens.borders.widths.thin,
  backgroundColor: designTokens.colors.border.light,
  width: '100%',
}

const showMoreButtonStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.inverse,
  ...typographyStyle(designTokens.typography.body.base),
}

export function PaymentHistoryPanel({
  items,
  variant = 'desktop',
  tone = 'default',
  initialVisibleItems,
  className,
}: PaymentHistoryPanelProps) {
  const isMobile = variant === 'mobile'
  const [isExpanded, setIsExpanded] = useState(false)

  const panelVariant = isMobile
    ? tone === 'catalog-dark'
      ? 'contentMobileCatalog'
      : 'contentMobile'
    : 'transparentPanel'
  const panelGap = isMobile ? 'none' : '24'
  const hasVisibleLimit =
    !isMobile && typeof initialVisibleItems === 'number' && initialVisibleItems > 0
  const visibleItems = hasVisibleLimit && !isExpanded ? items.slice(0, initialVisibleItems) : items
  const shouldShowToggle = hasVisibleLimit && items.length > initialVisibleItems

  return (
    <ContentGroup
      variant={panelVariant}
      gap={panelGap}
      justify="start"
      surface={isMobile && tone !== 'catalog-dark' ? 'glass-50' : undefined}
      border={isMobile && tone !== 'catalog-dark' ? 'glass' : undefined}
      radius={isMobile && tone !== 'catalog-dark' ? 'md' : undefined}
      className={cn('w-full', className)}
      style={
        !isMobile && tone === 'catalog-dark'
          ? {
              background: designTokens.colors.background.catalogSubscriptionPanel,
              borderColor: designTokens.colors.border.catalogSubscriptionPanel,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            }
          : undefined
      }
    >
      {!isMobile ? (
        <div className="flex w-full items-start justify-between" style={{ gap: '16px' }}>
          <p style={tone === 'catalog-dark' ? { ...titleStyle, color: '#FEFEFE' } : titleStyle}>
            История платежей
          </p>
          {shouldShowToggle ? (
            <button
              type="button"
              className="shrink-0 cursor-pointer"
              style={showMoreButtonStyle}
              onClick={() => setIsExpanded((current) => !current)}
            >
              {isExpanded ? 'Свернуть' : 'Показать больше'}
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        className="flex w-full flex-col"
        style={{ gap: isMobile ? designTokens.spacing.none : designTokens.spacing.md }}
      >
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1
          const rowPaddingTop = index === 0 ? designTokens.spacing.none : designTokens.spacing.md
          const rowPaddingBottom = index === 0 ? designTokens.spacing.xs : designTokens.spacing.md

          return (
            <div
              key={`${item.date}-${item.planName}-${index}`}
              className="flex w-full flex-col"
              style={{
                gap: isMobile ? designTokens.spacing.xxs : designTokens.spacing.md,
                paddingTop: isMobile ? rowPaddingTop : designTokens.spacing.none,
                paddingBottom: isMobile ? rowPaddingBottom : designTokens.spacing.none,
                borderBottomWidth:
                  isMobile && !isLast
                    ? designTokens.borders.widths.thin
                    : designTokens.borders.widths.none,
                borderBottomStyle:
                  isMobile && !isLast ? (designTokens.borders.styles.solid as 'solid') : undefined,
                borderBottomColor: isMobile
                  ? tone === 'catalog-dark'
                    ? designTokens.colors.border.catalogSubscriptionPanel
                    : designTokens.colors.border.paymentHistoryDivider
                  : designTokens.colors.background.transparent,
              }}
            >
              <PaymentHistoryRow
                status={item.status}
                date={item.date}
                planName={item.planName}
                amount={item.amount}
                variant={variant}
                tone={tone}
              />
              {!isMobile && !isLast ? (
                <div
                  style={
                    tone === 'catalog-dark'
                      ? { ...dividerStyle, backgroundColor: 'rgba(255, 255, 255, 0.2)' }
                      : dividerStyle
                  }
                  aria-hidden="true"
                />
              ) : null}
            </div>
          )
        })}
      </div>
    </ContentGroup>
  )
}

PaymentHistoryPanel.displayName = 'PaymentHistoryPanel'
